'use client';

import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiRoutes, getApiUrl } from '@/constants/api';
import { useRecoveryStore } from '@/hooks/useRecoveryStore';
import { BadRequestError, NotFoundError } from '@/lib/api/errors/api-errors';
import { clientApiFetcher } from '@/lib/api/fetcher/client';
import { cn } from '@/lib/utils';

export function ForgotPasswordForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  const [inputEmail, setInputEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const storeEmail = useRecoveryStore((state) => state.setEmail);
  const [loading, setLoading] = useState(false);

  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await clientApiFetcher(getApiUrl(apiRoutes.auth.sendRecoveryOtp), {
        method: 'POST',
        body: JSON.stringify({ email: inputEmail }),
        skipRefresh: true,
      });
      storeEmail(inputEmail);
      router.push('./reset-password');
    } catch (error: unknown) {
      if (error instanceof BadRequestError) {
        setError('Email is required');
      } else if (error instanceof NotFoundError) {
        setError(
          "We couldn't find an account associated with this email address",
        );
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className={cn('space-y-4', className)} {...props}>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <div className='relative'>
            <Mail className='absolute left-3 top-3 size-4 text-brand-green' />
            <Input
              type='email'
              placeholder='Email address'
              value={inputEmail}
              onChange={(e) => {
                setInputEmail(e.target.value);
                setError(null);
              }}
              className='border-brand-green pl-10 focus:border-brand-green focus:ring-brand-green'
              required
            />
          </div>
        </div>
        {error && (
          <div className='text-sm text-red-500'>
            {error}
            {error.includes("We couldn't find an account") && (
              <>
                {' '}
                <Link
                  href='/auth/signup'
                  className='text-brand-green transition-colors hover:text-brand-base'
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        )}
        <Button
          type='submit'
          variant='default'
          disabled={loading}
          className='w-full'
        >
          {loading ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </form>
    </div>
  );
}
